import {
  formatMetadataChangeDetail,
  isCurveChangeType,
  isFormulaMetadataChangeType,
  isPermissionChangeType,
  mergeFormulaMetadataChangeType,
  productChangeSessionKey,
  productChangeSidebarKey,
  type FolderAccess,
  type ProductChangeAlert,
  type VisiblePermissionChangeDirection,
} from "../calcs/lib/features/product-alerts";

export type CurveChangeBodyLine = {
  text: string;
  showViewFormula?: boolean;
  /** Clamp body copy (e.g. metadata field list) to N lines per Figma. */
  clampLines?: number;
};

/**
 * Losing a folder outright is reported as a single anonymous card counting the folders, because
 * naming a folder the user can no longer see would tell them something they've lost the right to
 * know. Every other direction leaves the folder listed, so those cards name it and its curves.
 */
export type PermissionChangeGroup =
  | {
      kind: "removed";
      key: string;
      folderCount: number;
      curves: ProductChangeAlert[];
      /** Most recent `createdAt` among the removed folders. */
      createdAt: number;
    }
  | {
      kind: "changed";
      key: string;
      folderName: string;
      direction: VisiblePermissionChangeDirection;
      fromAccess?: FolderAccess;
      toAccess?: FolderAccess;
      /** Total curves in the folder, which can exceed the capped list of alerts. */
      folderCurveCount?: number;
      curves: ProductChangeAlert[];
      /** Most recent `createdAt` among the group's curves. */
      createdAt: number;
    };

export const splitProductChangeAlerts = (alerts: ProductChangeAlert[]) => ({
  curveChanges: alerts.filter((alert) => isCurveChangeType(alert.changeType)),
  permissionChanges: alerts.filter((alert) => isPermissionChangeType(alert.changeType)),
});

export const formatCurveChangeCardTitle = (curveName: string, folderName: string) => {
  const folder = folderName.trim() || "Unknown folder";
  return `${curveName.trim()}: ${folder}`;
};

const sourceFolderFromDetail = (detail?: string) =>
  detail?.match(/from "([^"]+)"/)?.[1] ?? detail?.match(/from (.+?) to /)?.[1];

const destinationFolderFromDetail = (detail?: string) => {
  const into = detail?.match(/into (.+)$/i)?.[1]?.trim();
  if (into) return into;
  const to = detail?.match(/ to (.+)$/i)?.[1]?.trim();
  if (to && to.toLowerCase() !== "a new folder") return to;
  return undefined;
};

const usableLiveFolderName = (liveFolderName: string) => {
  const trimmed = liveFolderName.trim();
  return trimmed && trimmed !== "Unknown folder" ? trimmed : "";
};

export const getCurveChangeTitleFolder = (alert: ProductChangeAlert, liveFolderName: string) => {
  const moved = alert.changeType === "curve-moved" || alert.fromFolderName !== undefined;
  if (!moved) return liveFolderName;
  const moveCopy = alert.changeType === "curve-moved" ? alert.detail : alert.moveDetail;
  return (
    alert.fromFolderName?.trim() ||
    sourceFolderFromDetail(moveCopy)?.trim() ||
    destinationFolderFromDetail(moveCopy) ||
    usableLiveFolderName(liveFolderName) ||
    liveFolderName
  );
};

export const getCurveChangeBodyLines = (alert: ProductChangeAlert): CurveChangeBodyLine[] => {
  const { changeType, detail } = alert;
  const moveCopy = changeType === "curve-moved" ? detail : alert.moveDetail;
  const moveLines: CurveChangeBodyLine[] = moveCopy
    ? [{ text: moveCopy.replace(/^Moved/i, "This Curve was moved"), clampLines: 2 }]
    : changeType === "curve-moved"
      ? [{ text: "This Curve was moved to another folder", clampLines: 2 }]
      : [];

  if (changeType === "curve-moved") return moveLines;

  const formulaLines: CurveChangeBodyLine[] =
    changeType === "formula"
      ? [{ text: "Curve formula changed", showViewFormula: true }]
      : changeType === "metadata"
        ? [{ text: detail ?? "Metadata changed", clampLines: 2 }]
        : changeType === "both"
          ? [
              { text: detail ?? "Metadata changed", clampLines: 2 },
              { text: "Curve formula changed", showViewFormula: true },
            ]
          : [{ text: detail ?? "Curve changed" }];

  return moveLines.length > 0 ? [...moveLines, ...formulaLines] : formulaLines;
};

const PERMISSION_CURVE_NAMES_SHOWN = 3;

const ACCESS_LABEL: Record<FolderAccess, string> = {
  none: "None",
  list: "List",
  read: "Read",
  write: "Write",
  broadcast: "Broadcast",
  other: "Other",
};

export const formatPermissionRemovedTitle = (folderCount: number) =>
  folderCount > 1 ? `You have lost permissions to ${folderCount} Folders` : "You have lost permissions to a Folder";

export const formatPermissionCardTitle = (
  folderName: string,
  direction: VisiblePermissionChangeDirection,
  fromAccess?: FolderAccess,
  toAccess?: FolderAccess,
) => {
  if (direction === "gained") {
    // Names the level actually granted (List/Read/Broadcast/Write) rather than a generic "access".
    return toAccess
      ? `You have gained ${ACCESS_LABEL[toAccess]} permissions to ${folderName}`
      : `You have gained access to ${folderName}`;
  }
  // Every level-to-level move shares one title template; the pair makes each one distinct.
  return `Permissions to ${folderName} changed from ${ACCESS_LABEL[fromAccess ?? "read"]} to ${ACCESS_LABEL[toAccess ?? "write"]}`;
};

/**
 * Exact body copy per (from, to) pair on the access ladder (list < read < broadcast < write), per
 * design spec — not derivable from the coarse direction bucket, since e.g. list -> read and
 * read -> broadcast are both "upgraded" but say very different things.
 */
const PERMISSION_TRANSITION_BODY: Partial<Record<FolderAccess, Partial<Record<FolderAccess, string>>>> = {
  list: {
    read: "You can now view data for ",
    broadcast: "You can now view and input data for ",
    write: "You can now edit, view, and input data for ",
  },
  read: {
    list: "You can no longer view data for ",
    broadcast: "You can now input data for ",
    write: "You can now edit and input data for ",
  },
  broadcast: {
    list: "You can no longer view or input data for ",
    read: "You can no longer input data for ",
    write: "You can now edit ",
  },
  write: {
    list: "You can no longer edit, view, or input data for ",
    read: "You can no longer edit or input data for ",
    broadcast: "You can no longer edit ",
  },
};

/** Body prefix for a first-time grant, keyed by the level actually reached. */
const GAINED_BODY_PREFIX: Partial<Record<FolderAccess, string>> = {
  list: "You have limited access to ",
  read: "You can now view data for ",
  broadcast: "You can now view and input data for ",
  write: "You can now edit, view, and input data for ",
};

const permissionBodyPrefix = (
  direction: VisiblePermissionChangeDirection,
  fromAccess?: FolderAccess,
  toAccess?: FolderAccess,
): string => {
  if (direction === "gained") {
    return (toAccess && GAINED_BODY_PREFIX[toAccess]) || "You can now view and use ";
  }
  const exact = fromAccess && toAccess ? PERMISSION_TRANSITION_BODY[fromAccess]?.[toAccess] : undefined;
  if (exact) return exact;
  // Older persisted alerts predating fromAccess/toAccess: fall back to a reasonable default.
  return direction === "downgraded" || direction === "restricted"
    ? "You can no longer view or use "
    : "You can now edit and input data for ";
};

export const formatPermissionCurvesBody = (
  curveNames: string[],
  direction: VisiblePermissionChangeDirection = "restricted",
  folderCurveCount?: number,
  toAccess?: FolderAccess,
  fromAccess?: FolderAccess,
) => {
  const unique = [...new Set(curveNames.map((name) => name.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const prefix = permissionBodyPrefix(direction, fromAccess, toAccess);

  if (unique.length === 0) return `${prefix}this curve`;

  const shown = unique.slice(0, PERMISSION_CURVE_NAMES_SHOWN);
  // Alerts are capped per folder, so the folder's own count is what makes the tail honest; fall
  // back to the alerts in hand when it's missing (older persisted alerts, page-level detection).
  const others = Math.max(folderCurveCount ?? unique.length, unique.length) - shown.length;

  if (others <= 0) return `${prefix}${shown.join(", ")}`;
  return `${prefix}${shown.join(", ")} and ${others} other${others === 1 ? "" : "s"}`;
};

const folderNameFromDetail = (detail?: string) => detail?.match(/folder "([^"]+)"/)?.[1];

const REMOVED_GROUP_KEY = "permissions-removed";

export const groupPermissionChanges = (
  alerts: ProductChangeAlert[],
  folderNameByPackageId: (packageId: string | null | undefined) => string | undefined,
): PermissionChangeGroup[] => {
  const removed: ProductChangeAlert[] = [];
  /** Folders are counted, not listed — an alert with no package id can only be counted once. */
  const removedFolderKeys = new Set<string>();
  const groups = new Map<string, Extract<PermissionChangeGroup, { kind: "changed" }>>();

  for (const alert of alerts) {
    const direction = alert.permissionChangeDirection ?? "lost";

    if (direction === "lost") {
      removed.push(alert);
      removedFolderKeys.add(alert.packageId ?? "unknown-folder");
      continue;
    }

    const folderKey = alert.packageId ?? `detail:${folderNameFromDetail(alert.detail) ?? alert.name}`;
    // Key on the exact levels too — distinct pairs like list -> read and read -> broadcast can
    // share a direction but need separate cards.
    const key = `${folderKey}:${alert.fromAccess ?? "-"}:${alert.toAccess ?? "-"}:${direction}`;
    const folderName =
      (alert.packageId ? folderNameByPackageId(alert.packageId) : undefined) ??
      folderNameFromDetail(alert.detail) ??
      "Unknown folder";

    const existing = groups.get(key);
    if (existing) {
      existing.curves.push(alert);
      const count = Math.max(existing.folderCurveCount ?? 0, alert.folderCurveCount ?? 0);
      existing.folderCurveCount = count > 0 ? count : undefined;
      existing.createdAt = Math.max(existing.createdAt, alert.createdAt ?? 0);
      continue;
    }

    groups.set(key, {
      kind: "changed",
      key,
      folderName,
      direction,
      fromAccess: alert.fromAccess,
      toAccess: alert.toAccess,
      folderCurveCount: alert.folderCurveCount,
      curves: [alert],
      createdAt: alert.createdAt ?? 0,
    });
  }

  const groupList: PermissionChangeGroup[] = [...groups.values()];
  if (removed.length > 0) {
    const createdAt = removed.reduce((max, alert) => Math.max(max, alert.createdAt ?? 0), 0);
    groupList.push({
      kind: "removed",
      key: REMOVED_GROUP_KEY,
      folderCount: removedFolderKeys.size,
      curves: removed,
      createdAt,
    });
  }

  // Newest change leads — removal is just another card here, not pinned to the top.
  return groupList.sort((a, b) => b.createdAt - a.createdAt);
};

export const curveChangeSortKey = (alert: ProductChangeAlert) => productChangeSidebarKey(alert).toLocaleLowerCase();

export const productChangeToastTitle = (count: number) => (count > 1 ? "Multiple Curve Changes" : "Curve changed");

/** Distinct curves on a toast — same-curve repeats must not become "Multiple Curve Changes". */
export const productChangeToastCurveCount = (alerts: readonly ProductChangeAlert[]) =>
  new Set(alerts.map((alert) => productChangeSessionKey(alert))).size;

const unionChangedFieldLabels = (...groups: Array<readonly string[] | undefined>) => {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    for (const label of group ?? []) {
      const trimmed = label.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      labels.push(trimmed);
    }
  }
  return labels;
};

const mergeToastChangeType = (
  current: ProductChangeAlert["changeType"],
  next: ProductChangeAlert["changeType"],
): ProductChangeAlert["changeType"] => {
  if (isFormulaMetadataChangeType(current) && isFormulaMetadataChangeType(next)) {
    return mergeFormulaMetadataChangeType(current, next);
  }
  if (isFormulaMetadataChangeType(current)) return current;
  if (isFormulaMetadataChangeType(next)) return next;
  return next;
};

const toastMoveDetail = (prev: ProductChangeAlert, next: ProductChangeAlert) => {
  if (prev.changeType === "curve-moved") return prev.detail;
  if (next.changeType === "curve-moved") return next.detail;
  return next.moveDetail ?? prev.moveDetail;
};

/** Collapse later edits of the same curve into one toast row covering every change so far. */
const mergeSameCurveToastAlert = (prev: ProductChangeAlert, next: ProductChangeAlert): ProductChangeAlert => {
  const changeType = mergeToastChangeType(prev.changeType, next.changeType);
  const changedFieldLabels = unionChangedFieldLabels(prev.changedFieldLabels, next.changedFieldLabels);
  const hasMetadata = changeType === "metadata" || changeType === "both";
  const detail =
    hasMetadata && changedFieldLabels.length > 0
      ? formatMetadataChangeDetail(changedFieldLabels)
      : (next.detail ?? prev.detail);
  const fromFolderName = next.fromFolderName !== undefined ? next.fromFolderName : prev.fromFolderName;
  const moveDetail = toastMoveDetail(prev, next);

  return {
    ...prev,
    ...next,
    productId: next.productId ?? prev.productId,
    name: next.name || prev.name,
    packageId: next.packageId ?? prev.packageId,
    changeType,
    changedFieldLabels:
      changedFieldLabels.length > 0 ? changedFieldLabels : (next.changedFieldLabels ?? prev.changedFieldLabels),
    detail,
    fromFolderName,
    moveDetail,
    createdAt: prev.createdAt ?? next.createdAt,
  };
};

/**
 * Keep the open toast's curves and union a later batch, instead of replacing A with B.
 * Repeats of the same curve update that row so the copy covers every change so far.
 */
export const aggregateOpenProductChangeToast = (
  openToastProducts: readonly ProductChangeAlert[],
  incomingCurves: readonly ProductChangeAlert[],
): ProductChangeAlert[] => {
  if (openToastProducts.length === 0) return [...incomingCurves];

  const merged = [...openToastProducts];
  for (const incoming of incomingCurves) {
    const key = productChangeSessionKey(incoming);
    const existingIndex = merged.findIndex((alert) => productChangeSessionKey(alert) === key);
    if (existingIndex === -1) {
      merged.push(incoming);
      continue;
    }
    merged[existingIndex] = mergeSameCurveToastAlert(merged[existingIndex]!, incoming);
  }
  return merged;
};

/** Toast copy: moves only show curve + source folder; the card carries the full sentence. */
export const getCurveChangeToastCopy = (
  alert: ProductChangeAlert,
  curveName: string,
  folderName: string,
): { header: string; body?: string } => {
  const header = formatCurveChangeCardTitle(curveName, getCurveChangeTitleFolder(alert, folderName));
  if (alert.changeType === "curve-moved") {
    return { header };
  }
  return {
    header,
    body: getCurveChangeBodyLines(alert)
      .map((line) => line.text)
      .join("\n"),
  };
};
